import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Bus, MapPin, Map as MapIcon, Plane, Minimize2, Maximize2, Circle, Flag, Building2, Truck, Smartphone } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  TrackingLiveResponse,
  TrackingPositionDto,
  FlightPositionDto,
  getLiveData,
  getHistory,
  getPlannedRoute,
  getNavRoute,
  getFlightPosition,
} from '../services/trackingApi';

// Bus icon
const busIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/3448/3448339.png',
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});

// Destination icon (red marker)
const destinationIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/684/684908.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Waypoint icon (small blue dot)
const waypointIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/7893/7893979.png',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -10],
});

// Plane icon
const planeIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/3125/3125713.png',
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -18],
});

// Auto-center on first load only
const MapInitCenter: React.FC<{ lat: number; lng: number }> = ({ lat, lng }) => {
  const map = useMap();
  const initialised = useRef(false);
  useEffect(() => {
    if (initialised.current) return;
    if (lat && lng) { map.setView([lat, lng], 13); initialised.current = true; }
  }, [lat, lng, map]);
  return null;
};

// Expose map instance to parent via ref
const MapRefSetter: React.FC<{ onMap: (map: L.Map) => void }> = ({ onMap }) => {
  const map = useMap();
  useEffect(() => { onMap(map); }, [map, onMap]);
  return null;
};

const LiveTrackingPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [live, setLive] = useState<TrackingLiveResponse | null>(null);
  const [positions, setPositions] = useState<TrackingPositionDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [plannedRoute, setPlannedRoute] = useState<any>(null);
  const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
  const setMapRef = useCallback((map: L.Map) => setMapInstance(map), []);

  const [mapExpanded, setMapExpanded] = useState(true);

  // Navigation route (bus → target)
  const [navRoute, setNavRoute] = useState<any>(null); // { route, target }

  // Flight tracking
  const [flightPos, setFlightPos] = useState<FlightPositionDto | null>(null);

  // Track current target to detect changes
  const lastTargetRef = useRef<number | null | undefined>(undefined);

  // Fetch planned route (re-fetch when target changes)
  const loadPlannedRoute = useCallback(() => {
    if (!token) return;
    getPlannedRoute(token).then(data => {
      if (data && data.segments && data.segments.length > 0) {
        setPlannedRoute(data);
      }
    }).catch(() => {});
  }, [token]);

  useEffect(() => { loadPlannedRoute(); }, [loadPlannedRoute]);

  // Poll every 10s
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const fetchData = async () => {
      try {
        const [liveData, histData] = await Promise.all([
          getLiveData(token),
          getHistory(token),
        ]);
        if (cancelled) return;
        setLive(liveData);
        setPositions(histData.positions || []);
        setError(null);

        // Detect target change → reload route
        if (lastTargetRef.current !== undefined && lastTargetRef.current !== liveData.targetSegmentId) {
          loadPlannedRoute();
        }
        lastTargetRef.current = liveData.targetSegmentId;

        // Load navigation route (bus → target) if bus position + target exist
        if (liveData.currentPosition && liveData.targetSegmentId) {
          try {
            const nav = await getNavRoute(token);
            if (!cancelled && nav?.route) setNavRoute(nav);
            else if (!cancelled) setNavRoute(null);
          } catch { if (!cancelled) setNavRoute(null); }
        } else if (!cancelled) {
          setNavRoute(null);
        }
        // Load flight position
        try {
          const flightData = await getFlightPosition(token);
          if (!cancelled) setFlightPos(flightData?.flight ?? null);
        } catch { if (!cancelled) setFlightPos(null); }
      } catch (err: any) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [token, loadPlannedRoute]);

  // Invalidate map size when toggling expanded/collapsed
  useEffect(() => {
    if (mapInstance) {
      setTimeout(() => mapInstance.invalidateSize(), 100);
    }
  }, [mapExpanded, mapInstance]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column' }}>
        <div style={{ width: 40, height: 40, border: '4px solid #f3f3f3', borderTop: '4px solid #007bff', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '1rem' }} />
        <p>Lade Live-Tracking...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', color: '#dc3545' }}>
        <h2>Tracking nicht verfügbar</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!live) return null;

  const currentPos = live.currentPosition;
  const routeCoords: [number, number][] = positions.map(p => [p.latitude, p.longitude]);
  const defaultCenter: [number, number] = currentPos
    ? [currentPos.latitude, currentPos.longitude]
    : routeCoords.length > 0
      ? routeCoords[routeCoords.length - 1]
      : [51.1657, 10.4515]; // Germany center

  // Compute bus position for button
  const busPos = currentPos
    ? { lat: currentPos.latitude, lng: currentPos.longitude }
    : null;

  // Compute destination from planned route
  const destSeg = plannedRoute?.segments?.length
    ? plannedRoute.segments.filter((s: any) => s.coordinates).slice(-1)[0]
    : null;
  const destinationPos = destSeg
    ? { lat: destSeg.coordinates.latitude, lng: destSeg.coordinates.longitude, name: destSeg.location }
    : null;

  // Haversine distance helper (km)
  const haversineKm = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // Flight distance to destination
  const flightDistKm = flightPos && destinationPos
    ? haversineKm(flightPos.latitude, flightPos.longitude, destinationPos.lat, destinationPos.lng)
    : null;

  // Compute bounds that include bus + all route points + all segments + test route
  let routeBounds: L.LatLngBounds | null = null;
  const allPoints: [number, number][] = [];
  if (busPos) allPoints.push([busPos.lat, busPos.lng]);
  if (routeCoords.length > 0) allPoints.push(...routeCoords);
  if (plannedRoute?.fullRoute?.points) {
    plannedRoute.fullRoute.points.forEach((p: any) => allPoints.push([p.latitude, p.longitude]));
  }
  plannedRoute?.segments?.forEach((s: any) => {
    if (s.coordinates) allPoints.push([s.coordinates.latitude, s.coordinates.longitude]);
  });
  if (navRoute?.route?.points) {
    navRoute.route.points.forEach((p: any) => allPoints.push([p.latitude, p.longitude]));
  }
  if (flightPos) {
    allPoints.push([flightPos.latitude, flightPos.longitude]);
  }
  if (allPoints.length >= 2) {
    routeBounds = L.latLngBounds(allPoints.map(p => L.latLng(p[0], p[1])));
  }

  // Active segment bounds: Bus → current target only (for "Tour" button)
  let activeBounds: L.LatLngBounds | null = null;
  const activePoints: [number, number][] = [];
  if (busPos) activePoints.push([busPos.lat, busPos.lng]);
  if (destinationPos) activePoints.push([destinationPos.lat, destinationPos.lng]);
  if (navRoute?.route?.points) {
    navRoute.route.points.forEach((p: any) => activePoints.push([p.latitude, p.longitude]));
  }
  if (activePoints.length >= 2) {
    activeBounds = L.latLngBounds(activePoints.map(p => L.latLng(p[0], p[1])));
  }

  const headerBtnStyle: React.CSSProperties = {
    padding: '0.35rem 0.65rem', border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '6px', backgroundColor: 'rgba(255,255,255,0.15)',
    color: 'white', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
    whiteSpace: 'nowrap', transition: 'background-color 0.15s',
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      ...(mapExpanded
        ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, backgroundColor: 'white' }
        : { minHeight: '100vh' })
    }}>
      {/* Single combined banner (green/red) */}
      <div style={{
        padding: '0.35rem 0.75rem',
        backgroundColor: live.status === 'Active' ? '#27ae60' : '#e74c3c',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
        fontSize: '0.85rem',
        gap: '0.5rem',
        flexWrap: 'wrap',
      }}>
        {/* Left: Trip name + Destination + coords + distance */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minWidth: 0, flexWrap: 'wrap' }}>
          <strong style={{ whiteSpace: 'nowrap' }}><Bus size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {live.name}</strong>
          <span style={{ whiteSpace: 'nowrap', opacity: 0.9, fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 3, padding: '0.1rem 0.4rem', borderRadius: 4, backgroundColor: 'rgba(255,255,255,0.18)' }}>
            {live.source === 'Dako'
              ? <><Truck size={12} /> Fahrzeug/DAKO</>
              : <><Smartphone size={12} /> Handy</>}
          </span>
          {destinationPos && (
            <span style={{ whiteSpace: 'nowrap', opacity: 0.95 }}><MapPin size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Ziel: {destinationPos.name}</span>
          )}
          {currentPos && (
            <span style={{ opacity: 0.85, whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
              ({currentPos.latitude.toFixed(4)}, {currentPos.longitude.toFixed(4)})
            </span>
          )}
          {navRoute?.route?.summary && (
            <span style={{ opacity: 0.85, whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
              · {navRoute.route.summary.totalDistanceKm.toFixed(1)} km · {Math.round(navRoute.route.summary.totalTimeMinutes)} min
            </span>
          )}
        </div>

        {/* Right: Buttons + Status + Timestamp */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0, flexWrap: 'wrap' }}>
          {busPos && mapInstance && (
            <button onClick={() => mapInstance.setView([busPos.lat, busPos.lng], 15)} title="Bus zentrieren" style={headerBtnStyle}><Bus size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} /> Bus</button>
          )}
          {(activeBounds || routeBounds) && mapInstance && (
            <button onClick={() => mapInstance.fitBounds((activeBounds || routeBounds) as L.LatLngBoundsExpression, { padding: [40, 40] })} title="Aktive Strecke anzeigen" style={headerBtnStyle}><MapIcon size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} /> Tour</button>
          )}
          {destinationPos && mapInstance && (
            <button onClick={() => mapInstance.setView([destinationPos.lat, destinationPos.lng], 15)} title={`Ziel: ${destinationPos.name}`} style={headerBtnStyle}><MapPin size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} /> Ziel</button>
          )}
          {flightPos && mapInstance && (
            <button onClick={() => mapInstance.setView([flightPos.latitude, flightPos.longitude], 10)} title={`Flug: ${flightPos.flightNumber}`} style={{ ...headerBtnStyle, backgroundColor: 'rgba(255,193,7,0.35)' }}><Plane size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} /> Flug</button>
          )}
          <button
            onClick={() => {
              setMapExpanded(prev => !prev);
              setTimeout(() => mapInstance?.invalidateSize(), 50);
            }}
            title={mapExpanded ? 'Karte verkleinern' : 'Karte maximieren'}
            style={headerBtnStyle}
          >{mapExpanded ? <><Minimize2 size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} /> Klein</> : <><Maximize2 size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} /> Max</>}</button>

          <span style={{ fontWeight: 700, whiteSpace: 'nowrap', marginLeft: '0.4rem' }}>
            {live.status === 'Active' ? <><Circle size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Live</> : <><Circle size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Beendet</>}
          </span>
          {live.status === 'Active' && currentPos?.timestamp && (
            <span style={{ opacity: 0.9, whiteSpace: 'nowrap', fontSize: '0.78rem' }}>
              Letzte Aktualisierung: {new Date(currentPos.timestamp).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
        </div>
      </div>

      {/* Map */}
      <div style={{ flex: 1, minHeight: mapExpanded ? 0 : '300px' }}>
        <MapContainer center={defaultCenter} zoom={13} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MapRefSetter onMap={setMapRef} />
          {busPos && <MapInitCenter lat={busPos.lat} lng={busPos.lng} />}

          {currentPos && (
            <Marker position={[currentPos.latitude, currentPos.longitude]} icon={busIcon}>
              <Popup>
                <strong>Bus – Aktuelle Position</strong><br />
                {currentPos.speed != null && <>Geschwindigkeit: {Math.round(currentPos.speed * 3.6)} km/h<br /></>}
                {currentPos.accuracy != null && <>Genauigkeit: {Math.round(currentPos.accuracy)} m<br /></>}
                Letztes Update: {new Date(currentPos.timestamp).toLocaleTimeString('de-DE')}
              </Popup>
            </Marker>
          )}
          {routeCoords.length > 1 && (
            <Polyline positions={routeCoords} color="#007bff" weight={4} opacity={0.7} />
          )}

          {/* Planned route polyline (from TomTom) */}
          {plannedRoute?.fullRoute?.points && plannedRoute.fullRoute.points.length > 1 && (
            <Polyline
              positions={plannedRoute.fullRoute.points.map((p: any) => [p.latitude, p.longitude])}
              color="#28a745"
              weight={5}
              opacity={0.6}
              dashArray="10 6"
            />
          )}

          {/* Navigation route: bus → target (cyan dashed) */}
          {navRoute?.route?.points && navRoute.route.points.length > 1 && (
            <Polyline
              positions={navRoute.route.points.map((p: any) => [p.latitude, p.longitude])}
              color="#00bcd4"
              weight={5}
              opacity={0.85}
              dashArray="12 8"
            />
          )}

          {/* Segment waypoint markers */}
          {plannedRoute?.segments?.map((seg: any, idx: number) => {
            if (!seg.coordinates) return null;
            const isLast = idx === plannedRoute.segments.length - 1;
            const isFirst = idx === 0;
            return (
              <Marker
                key={seg.segmentId}
                position={[seg.coordinates.latitude, seg.coordinates.longitude]}
                icon={isLast ? destinationIcon : waypointIcon}
              >
                <Popup>
                  <strong>{isFirst ? 'Start' : isLast ? 'Ziel' : `Stopp ${idx}`}</strong><br />
                  {seg.location}<br />
                  {seg.distanceToNextKm != null && !isLast && (
                    <span>→ {seg.distanceToNextKm.toFixed(1)} km zum nächsten Stopp</span>
                  )}
                </Popup>
              </Marker>
            );
          })}

          {/* Flight route to destination (dashed line) */}
          {flightPos && destinationPos && (
            <Polyline
              positions={[
                [flightPos.latitude, flightPos.longitude],
                [destinationPos.lat, destinationPos.lng],
              ]}
              color="#f39c12"
              weight={3}
              opacity={0.7}
              dashArray="8 6"
            />
          )}

          {/* Flight marker */}
          {flightPos && (
            <Marker position={[flightPos.latitude, flightPos.longitude]} icon={planeIcon}>
              <Popup>
                <strong>Flug {flightPos.flightNumber}</strong><br />
                {flightPos.callsign && <>Callsign: {flightPos.callsign}<br /></>}
                {flightPos.altitudeMeters != null && <>Höhe: {Math.round(flightPos.altitudeMeters)} m<br /></>}
                {flightPos.speedKmh != null && <>Geschwindigkeit: {Math.round(flightPos.speedKmh)} km/h<br /></>}
                {flightPos.onGround ? 'Am Boden' : 'In der Luft'}<br />
                {flightDistKm != null && <>Noch {Math.round(flightDistKm)} km zum Ziel<br /></>}
                Letztes Update: {new Date(flightPos.timestamp).toLocaleTimeString('de-DE')}
              </Popup>
            </Marker>
          )}
        </MapContainer>
      </div>


    </div>
  );
};

export default LiveTrackingPage;

